import type { ImageProps } from '@unpic/react';
import {
  Image as UnpicBaseImage,
  type ImageProps as UnpicBaseImageProps,
} from '@unpic/react/base';
import {
  getProviderForUrl,
  getTransformerForCdn,
  type Operations,
} from 'unpic';
import { getBreakpoints } from '@unpic/core';
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';
import { imageLoader } from '~/platform/platform';

/**
 * `fullWidth` carries no `width`, so unpic emits no `sizes` and the browser
 * falls back to `100vw` — right only for an image that genuinely spans the
 * viewport. Every other one silently downloads a candidate sized for the whole
 * screen, which went unnoticed at four call sites before US-12520.
 *
 * `constrained` and `fixed` derive a correct `sizes` from their own `width`.
 */
type RequireSizesWhenFullWidth =
  { layout?: 'constrained' | 'fixed' } | { layout: 'fullWidth'; sizes: string };

// The CDN and its options come from the platform, never the call site.
// Distributive, so each layout member keeps its own width/height rules.
type WithoutCdnChoice<T> = T extends unknown
  ? Omit<T, 'cdn' | 'fallback' | 'operations' | 'options'>
  : never;

type Props = WithoutCdnChoice<ImageProps> &
  RequireSizesWhenFullWidth & {
    /**
     * Rendered in place of the image if it fails to load, instead of the
     * browser's broken-image icon. Defaults to an empty `<span>` so the
     * surrounding container's own background fills the area.
     */
    placeholder?: ReactNode;
  };

/*
  Do not prefix `sizes` with `auto` here. It reads correctly — the browser
  picks a candidate from the element's real laid-out width — but it requires
  `width`/`height` attributes on the `<img>`, and unpic deletes those for both
  `fullWidth` and `constrained`. Without them the browser lays the image out at
  the default 300x150 object size and never consults the loaded image's
  intrinsic ratio, so every lazy image collapses to 150px tall. That shipped to
  a preview and squashed the lightbox.
*/

type Status = 'visible' | 'hidden' | 'fading-in' | 'error';

/**
 * A server-rendered image is visible from the first byte: the browser paints it
 * as its bytes arrive instead of waiting for the app to hydrate, which was the
 * largest measured delay to Largest Contentful Paint. On a slow connection it
 * can paint in gradually.
 *
 * An image first rendered in the browser (a client navigation, a growing list)
 * or given a new `src` there stays hidden until it has loaded, then fades in.
 * The fade is a keyframe animation because a `transition` from opacity 0 adds
 * its whole duration to LCP. A `priority` image, one already `complete` when it
 * mounts, and anyone who prefers reduced motion get no fade: the first is the
 * likely LCP element, the second is already there to show.
 *
 * On failure the image is dropped in favour of the placeholder. A
 * server-rendered image that fails before hydration shows the browser's
 * broken-image icon until the mount check swaps it out; we accept that window
 * rather than hide every server-rendered image until React runs.
 *
 * Hidden with opacity, not `display: none`: a `display: none` ancestor stops
 * lazily-loaded images — Unpic's default — from ever being fetched, which would
 * also deadlock the `load` event the reveal depends on.
 */
export function Image({ placeholder, onLoad, onError, ...props }: Props) {
  const ref = useRef<HTMLImageElement>(null);
  const serverRendered = useIsServerRendered();
  const [status, setStatus] = useState<Status>(
    serverRendered ? 'visible' : 'hidden'
  );

  // Reset when the source changes — important for `<Image>`s reused across list
  // items (gallery, grids). Done during render so we never briefly reveal the
  // old image under the new src.
  const prevSrc = useRef(props.src);
  if (prevSrc.current !== props.src) {
    prevSrc.current = props.src;
    setStatus('hidden');
  }

  // Pick up images that finished before React attached the handlers: cached
  // images (and the SSR'd markup on hydration) are often already `complete` on
  // mount, so their `load`/`error` events never fire for us.
  useEffect(() => {
    const img = ref.current;
    if (img?.complete) {
      setStatus(img.naturalWidth > 0 ? 'visible' : 'error');
    }
  }, [props.src]);

  if (status === 'error') {
    return placeholder ?? <span />;
  }

  const statusClassName = statusStyles[status];

  return (
    <UnpicImage
      {...props}
      breakpoints={allowedBreakpoints(props)}
      ref={ref}
      className={
        statusClassName ? cx(props.className, statusClassName) : props.className
      }
      onLoad={(event) => {
        // A server-rendered image has been on screen all along; fading it in
        // now would blank it first.
        setStatus((current) =>
          current === 'hidden'
            ? props.priority
              ? 'visible'
              : 'fading-in'
            : current
        );
        onLoad?.(event);
      }}
      onError={(event) => {
        setStatus('error');
        onError?.(event);
      }}
      transformer={optimizerTransformer(props.src)}
    />
  );
}

const statusStyles: Partial<Record<Status, string>> = {
  hidden: css({ opacity: '[0]' }),
  'fading-in': css({
    _motionSafe: { animation: 'image-fade-in 180ms ease-out' },
  }),
};

const subscribeToNothing = () => () => {};

/**
 * True on the server and while hydrating server HTML, false for an element
 * first rendered in the browser. React reads the server snapshot during
 * hydration, so the first client render matches the server's markup.
 *
 * Only the first render's value means anything: right after hydration React
 * re-renders with the browser snapshot, `false`. Use it to seed state, never
 * to decide anything on a later render.
 */
function useIsServerRendered() {
  return useSyncExternalStore(
    subscribeToNothing,
    () => false,
    () => true
  );
}

/**
 * The widths Vercel's image optimizer is configured to serve — `images.sizes`
 * in `apps/web-vnext/vercel.json`, which it 400s outside of.
 *
 * The build cannot read that JSON, so the copy is held to it by
 * `__test__/optimizer-widths.spec.ts`. A width that drifts out of the allowlist
 * is a broken image in production, not merely an oversized one.
 */
export const OPTIMIZER_WIDTHS = [
  48, 77, 96, 113, 154, 226, 256, 512, 640, 750, 828, 960, 1080, 1280, 1668,
  1920, 2048, 2560, 3200, 3840,
] as const;

const MAX_IMAGE_WIDTH = Math.max(...OPTIMIZER_WIDTHS);

/**
 * `fullWidth` has no display width for unpic to build `<img src>` from, and a
 * request without `w` is a 400, not a default size. Sized for a phone screen at
 * its full device pixel ratio.
 */
const FULL_WIDTH_SRC_WIDTH = 1080;

function optimizerWidth(width = FULL_WIDTH_SRC_WIDTH): number {
  return (
    OPTIMIZER_WIDTHS.find((allowed) => allowed >= width) ?? MAX_IMAGE_WIDTH
  );
}

/**
 * The base image, not unpic's default one, because only it takes a transformer.
 * React 19 passes a function component its `ref` as an ordinary prop and the
 * base image spreads every prop onto the `<img>`; only its types predate that.
 */
const UnpicImage = UnpicBaseImage as <TOperations extends Operations, TOptions>(
  props: UnpicBaseImageProps<TOperations, TOptions> & {
    ref?: React.Ref<HTMLImageElement>;
  }
) => ReturnType<typeof UnpicBaseImage>;

/**
 * unpic's transformer for the image's CDN, made to request only widths the
 * optimizer serves. unpic builds `<img src>` from the raw display width (none
 * at all for `fullWidth`), so on its own the `src` that crawlers and clients
 * ignoring srcset fetch is a 400.
 */
function optimizerTransformer(
  src: string
): (
  url: string | URL,
  operations: { width?: number; height?: number }
) => string {
  const cdn = getProviderForUrl(src) || imageLoader.fallback;
  const transformer = getTransformerForCdn(cdn);
  // Without a fallback (Storybook) an unrecognised host is served untouched.
  if (!cdn || !transformer) return (url) => url.toString();

  const options = imageLoader.options?.[cdn];
  return (url, { width, height, ...operations }) => {
    const snapped = optimizerWidth(width);
    return transformer(
      url,
      {
        ...operations,
        width: snapped,
        // Grown with the width, so a CDN that crops to both keeps the ratio.
        height: width && height && Math.round((height * snapped) / width),
      },
      options
    );
  };
}

/**
 * The srcset candidate widths unpic would generate, each rounded up to the
 * nearest width the optimizer serves — so no `_vercel/image` request can 400
 * on width, whatever display width a call site asks for.
 *
 * unpic derives candidates from layout + width (`fullWidth` up to 6016,
 * `constrained`/`fixed` the display width and 2× it). We reproduce that
 * derivation (or take the caller's explicit `breakpoints`) and snap it onto
 * the allowlist, clamping anything above it to the cap. An empty derivation
 * (e.g. a dimensionless layout) is passed through as `undefined` so unpic
 * keeps its own default behaviour.
 */
function allowedBreakpoints(props: ImageProps): number[] | undefined {
  // `width` only exists on the fixed/constrained members of the ImageProps
  // union — fullWidth has none — so read it through an `in` guard.
  const rawWidth = 'width' in props ? props.width : undefined;
  const width = typeof rawWidth === 'string' ? Number(rawWidth) : rawWidth;

  const derived =
    props.breakpoints ??
    getBreakpoints({
      layout: props.layout ?? 'constrained',
      width:
        typeof width === 'number' && Number.isFinite(width) ? width : undefined,
    });

  if (derived.length === 0) return undefined;

  // A fresh array, not `derived`: unpic's `getSrcSetEntries` sorts what it is
  // handed IN PLACE, which would reorder a caller's module-level constant — or
  // unpic's own `DEFAULT_RESOLUTIONS` — for everyone.
  return [...new Set(derived.map((w) => optimizerWidth(w)))];
}
